// src/adminDashboard/shared/components/DataTable/MobileCard.tsx
// Mobile card view for DataTable

import { ChevronRight } from 'lucide-react';
import React from 'react';

import { ColumnDef } from './utils/exportHelpers';

type RowData = object;
const toRecord = (row: RowData): Record<string, unknown> => row as Record<string, unknown>;

interface MobileCardProps<T extends RowData = RowData> {
    row: T;
    columns: ColumnDef[];
    onRowClick?: (row: T) => void;
    rowActions?: (row: T) => React.ReactNode;
    isSelected?: boolean;
    onSelectRow?: (rowId: string | number) => void;
    getId: (row: T) => string | number;
}

export const MobileCard = <T extends RowData = RowData,>({
    row,
    columns,
    onRowClick,
    rowActions,
    isSelected,
    onSelectRow,
    getId
}: MobileCardProps<T>) => {
    const rowId = getId(row);

    return (
        <div
            className={`bg-white rounded-lg border p-4 transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                } ${onRowClick ? 'cursor-pointer' : ''}`}
            onClick={() => onRowClick && onRowClick(row)}
        >
            {/* Selection Checkbox */}
            {onSelectRow && (
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow(rowId);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-xs font-medium text-slate-500">
                        {isSelected ? 'Selected' : 'Select'}
                    </span>
                </div>
            )}

            {/* Card Content */}
            <div className="space-y-3">
                {columns.map((column) => {
                    // Skip checkbox column
                    if (column.key === 'checkbox') return null;

                    return (
                        <div key={column.key} className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-500 mb-1">
                                    {column.header}
                                </p>
                                <div className="text-sm text-slate-900">
                                    {column.render ? column.render(row, 0) : (
                                        typeof toRecord(row)[column.key] === 'object'
                                            ? JSON.stringify(toRecord(row)[column.key] ?? '')
                                            : String(toRecord(row)[column.key] ?? '')
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Row Actions */}
            {rowActions && (
                <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Actions</span>
                    <div onClick={(e) => e.stopPropagation()}>
                        {rowActions(row)}
                    </div>
                </div>
            )}

            {/* Click Indicator */}
            {onRowClick && !rowActions && (
                <div className="flex items-center justify-end mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-primary flex items-center gap-1">
                        View Details
                        <ChevronRight size={14} />
                    </span>
                </div>
            )}
        </div>
    );
};

export default MobileCard;
